import { fetchDepthPoints } from "./depthApi";

export const DEPTH_DETAIL_ZOOM = 12;
export const DEPTH_DETAIL_METERS_PER_PIXEL = 10;
export const DEPTH_MAX_POINTS = 5000;

const BOUNDS_EPSILON = 1e-5;
const FETCH_PADDING_RATIO = 0.35;

export function emptyCache() {
    return {
        overview: {
            pointsById: new Map(),
            loadedBounds: null,
        },
        detail: {
            pointsById: new Map(),
            loadedBounds: null,
        },
        pendingFetches: new Map(),
    };
}

export function boundsSpanMeters(bounds) {
    const centerLat = (bounds.north + bounds.south) / 2;
    const latRad = (centerLat * Math.PI) / 180;
    const heightM = Math.abs(bounds.north - bounds.south) * 111320;
    const widthM = Math.abs(bounds.east - bounds.west) * 111320 * Math.cos(latRad);
    return {
        widthM,
        heightM,
        minSpan: Math.min(widthM, heightM),
        maxSpan: Math.max(widthM, heightM),
    };
}

export function getMetersPerPixel(bounds, zoom) {
    const centerLat = (bounds.north + bounds.south) / 2;
    const latRad = (centerLat * Math.PI) / 180;
    return (156543.03392 * Math.cos(latRad)) / (2 ** zoom);
}

export function getDepthLoadMode(viewport) {
    const zoom = Number(viewport?.zoom);
    const bounds = viewport?.bounds;
    if (!bounds || !Number.isFinite(zoom)) {
        return "detail";
    }
    const metersPerPixel = getMetersPerPixel(bounds, zoom);
    if (zoom >= DEPTH_DETAIL_ZOOM && metersPerPixel <= DEPTH_DETAIL_METERS_PER_PIXEL) {
        return "detail";
    }
    return "overview";
}

export function boundsContains(outer, inner) {
    if (!outer || !inner) return false;
    return (
        inner.north <= outer.north + BOUNDS_EPSILON
        && inner.south >= outer.south - BOUNDS_EPSILON
        && inner.east <= outer.east + BOUNDS_EPSILON
        && inner.west >= outer.west - BOUNDS_EPSILON
    );
}

function unionBounds(a, b) {
    if (!a) return { ...b };
    return {
        north: Math.max(a.north, b.north),
        south: Math.min(a.south, b.south),
        east: Math.max(a.east, b.east),
        west: Math.min(a.west, b.west),
    };
}

function padBounds(bounds, ratio = FETCH_PADDING_RATIO) {
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    const latPad = latSpan * ratio;
    const lngPad = lngSpan * ratio;
    return {
        north: bounds.north + latPad,
        south: bounds.south - latPad,
        east: bounds.east + lngPad,
        west: bounds.west - lngPad,
    };
}

function pointKey(point) {
    if (point?.id != null) return `id:${point.id}`;
    const lat = Number(point?.lat);
    const lng = Number(point?.lng);
    return `coord:${lat.toFixed(6)}:${lng.toFixed(6)}`;
}

function boundsFetchKey(bounds, mode) {
    return [
        mode,
        bounds.north.toFixed(5),
        bounds.south.toFixed(5),
        bounds.east.toFixed(5),
        bounds.west.toFixed(5),
    ].join("|");
}

function getModeCache(cache, mode) {
    return mode === "detail" ? cache.detail : cache.overview;
}

function mergePointsIntoCache(modeCache, points) {
    for (const point of points) {
        modeCache.pointsById.set(pointKey(point), point);
    }
}

export function filterPointsInBounds(points, bounds) {
    return points.filter((point) => {
        const lat = Number(point?.lat);
        const lng = Number(point?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        return (
            lat <= bounds.north + BOUNDS_EPSILON
            && lat >= bounds.south - BOUNDS_EPSILON
            && lng <= bounds.east + BOUNDS_EPSILON
            && lng >= bounds.west - BOUNDS_EPSILON
        );
    });
}

function shouldSkipFetch(modeCache, viewportBounds) {
    if (!modeCache.loadedBounds) {
        return false;
    }
    return boundsContains(modeCache.loadedBounds, viewportBounds);
}

function splitBounds(bounds) {
    const midLat = (bounds.north + bounds.south) / 2;
    const midLng = (bounds.east + bounds.west) / 2;
    return [
        { north: bounds.north, south: midLat, east: midLng, west: bounds.west },
        { north: bounds.north, south: midLat, east: bounds.east, west: midLng },
        { north: midLat, south: bounds.south, east: midLng, west: bounds.west },
        { north: midLat, south: bounds.south, east: bounds.east, west: midLng },
    ];
}

async function fetchBoundsRecursive(cache, modeCache, bounds, zoom, mode, minSpanM) {
    const fetchKey = boundsFetchKey(bounds, mode);
    const inFlight = cache.pendingFetches.get(fetchKey);
    if (inFlight) {
        await inFlight;
        return;
    }

    const fetchPromise = (async () => {
        const { points, truncated } = await fetchDepthPoints(bounds, zoom, { mode, minSpanM });
        mergePointsIntoCache(modeCache, points);

        if (truncated) {
            const quarters = splitBounds(bounds);
            await Promise.all(
                quarters
                    .filter((quarter) => (
                        Math.abs(quarter.north - quarter.south) >= BOUNDS_EPSILON
                        && Math.abs(quarter.east - quarter.west) >= BOUNDS_EPSILON
                    ))
                    .map((quarter) => fetchBoundsRecursive(cache, modeCache, quarter, zoom, mode, minSpanM)),
            );
        }

        modeCache.loadedBounds = unionBounds(modeCache.loadedBounds, bounds);
    })();

    cache.pendingFetches.set(fetchKey, fetchPromise);
    try {
        await fetchPromise;
    } finally {
        cache.pendingFetches.delete(fetchKey);
    }
}

export function getDepthPointsFromCache(cacheRef, viewport) {
    const bounds = viewport?.bounds;
    const zoom = Number(viewport?.zoom);
    if (!bounds || !Number.isFinite(zoom)) {
        return null;
    }

    const cache = cacheRef.current;
    if (!cache) {
        return null;
    }

    const mode = getDepthLoadMode(viewport);
    const modeCache = getModeCache(cache, mode);
    if (!shouldSkipFetch(modeCache, bounds)) {
        return null;
    }

    return filterPointsInBounds([...modeCache.pointsById.values()], bounds);
}

/**
 * Loads depth points for a viewport with per-mode caching and bbox subdivision when truncated.
 *
 * @param {object} cacheRef - React ref holding cache state
 * @param {{ bounds: object, zoom: number }} viewport
 * @returns {Promise<object[]>}
 */
export async function loadDepthPointsForViewport(cacheRef, viewport) {
    const bounds = viewport?.bounds;
    const zoom = Number(viewport?.zoom);
    if (!bounds || !Number.isFinite(zoom)) {
        return [];
    }

    const mode = getDepthLoadMode(viewport);
    const cache = cacheRef.current ?? emptyCache();
    cacheRef.current = cache;
    const modeCache = getModeCache(cache, mode);

    if (shouldSkipFetch(modeCache, bounds)) {
        return filterPointsInBounds([...modeCache.pointsById.values()], bounds);
    }

    const paddedBounds = padBounds(bounds);
    const { minSpan } = boundsSpanMeters(paddedBounds);
    await fetchBoundsRecursive(cache, modeCache, paddedBounds, zoom, mode, minSpan);

    return filterPointsInBounds([...modeCache.pointsById.values()], bounds);
}

export function resetDepthPointsCache(cacheRef) {
    cacheRef.current = emptyCache();
}
